import React, { useEffect, useState } from "react";
import { Eye, Download, Pencil, FileText, Plus, Trash, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import InvoiceUploadModal from "../../Models/Invoices/InvoiceUploadModal";
import InvoiceEditModal from "../../Models/Invoices/InvoiceEditModal";
import { useDispatch, useSelector } from "react-redux";
import { deleteMemberInvoice, getMemberInvoice } from "../../apis/api/InvoiceApi";
import Cookies from "js-cookie";
import Lodderr from "../../components/Lodder/Lodder";
import { Modal, Button } from "react-bootstrap";
import ConfirmationModal from "../../Models/ConfirmationModel/ConfirmationModel";
import { motion } from "framer-motion";
import "./InvoicePage.css";

export default function InvoicesPage() {
  const [statusFilter, setStatusFilter] = useState("All");
  const [uploadModel, setUploadModel] = useState(false);
  const [editInvoice, setEditInvoice] = useState(false);
  const [viewPdfUrl, setViewPdfUrl] = useState(null); // For PDF preview
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    invoiceId: null,
  });

  const dispatch = useDispatch();
  const companyId = Cookies.get("company_id");

  const { invoices = [], loading } = useSelector((state) => state.InvoiceSlice);

  useEffect(() => {
    dispatch(getMemberInvoice({ companyId }));
  }, [dispatch, companyId]);

  const totalCount = invoices.length;
  const paidCount = invoices.filter((inv) => String(inv.invoice_status) === "1").length;
  const unpaidCount = invoices.filter((inv) => String(inv.invoice_status) === "0").length;

  const filteredInvoices =
    statusFilter === "All"
      ? invoices
      : invoices.filter((inv) => String(inv.invoice_status) === statusFilter);

  const formatDate = (timestamp) => {
    if (!timestamp) return "--";
    return new Date(timestamp * 1000).toLocaleDateString("en-GB");
  };

  const confirmDelete = async () => {
    await dispatch(
      deleteMemberInvoice({
        companyId,
        invoiceId: confirmModal.invoiceId,
      })
    );
    dispatch(getMemberInvoice({ companyId }));
    setConfirmModal({ show: false, invoiceId: null });
  };

  return (
    <>
      {loading && <Lodderr />}

      {/* Upload Modal */}
      <InvoiceUploadModal show={uploadModel} handleClose={() => setUploadModel(false)} />

      {/* Edit Modal */}
      <InvoiceEditModal
        show={!!editInvoice}
        handleClose={() => setEditInvoice(null)}
        invoice={editInvoice}
      />

      <ConfirmationModal
        show={confirmModal.show}
        onHide={() => setConfirmModal({ show: false, invoiceId: null })}
        onConfirm={confirmDelete}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* PDF Preview Modal */}
      <Modal show={!!viewPdfUrl} size="xl" centered onHide={() => setViewPdfUrl(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Invoice Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewPdfUrl && (
            <object
              data={viewPdfUrl}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview not available.{" "}
                <a href={viewPdfUrl} target="_blank" rel="noreferrer">
                  Open PDF
                </a>
              </p>
            </object>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewPdfUrl(null)}>
            Close
          </Button>
          <a href={viewPdfUrl} target="_blank" download className="btn btn-primary">
            Download PDF
          </a>
        </Modal.Footer>
      </Modal>

      {/* Main Page */}
      <motion.div
        className="invoice-page ip-wrap"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 0.68, 0, 1.2] }}
      >
        {/* HEADER */}
        <div className="ip-hero">
          <div>
            <h1 className="ip-title">Invoices</h1>
            <p className="ip-sub">Upload, edit, and track invoices in a clean premium view.</p>
          </div>

          <motion.button
            className="ip-primary-btn"
            onClick={() => setUploadModel(true)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
          >
            <Plus size={16} /> Create Invoice
          </motion.button>
        </div>

        {/* FILTERS */}
        <div className="ip-filters">
          <button
            className={`ip-filter ${statusFilter === "All" ? "active" : ""}`}
            onClick={() => setStatusFilter("All")}
            type="button"
          >
            All {totalCount > 0 ? `(${totalCount})` : ""}
          </button>
          <button
            className={`ip-filter ${statusFilter === "1" ? "active paid" : ""}`}
            onClick={() => setStatusFilter("1")}
            type="button"
          >
            Paid {paidCount > 0 ? `(${paidCount})` : ""}
          </button>
          <button
            className={`ip-filter ${statusFilter === "0" ? "active unpaid" : ""}`}
            onClick={() => setStatusFilter("0")}
            type="button"
          >
            Unpaid {unpaidCount > 0 ? `(${unpaidCount})` : ""}
          </button>
        </div>

        {/* INVOICE CARDS / EMPTY STATE */}
        {filteredInvoices.length > 0 ? (
          <div className="ip-grid">
            {filteredInvoices.map((invoice, index) => (
              <motion.div
                key={invoice.id}
                className={`ip-card ${invoice.invoice_status === 1 ? "paid" : "unpaid"}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.35, ease: [0.22, 0.68, 0, 1.2] }}
                whileHover={{ y: -6 }}
              >
                <div className="card-top">
                  <div className="invoice-icon"><FileText size={18} /></div>
                  <span className={`status ${invoice.invoice_status === 1 ? "paid-status" : "unpaid-status"}`}>
                    {invoice.invoice_status === 1 ? "Paid" : "Unpaid"}
                  </span>
                </div>

                <div className="card-body-custom">
                  <h6 className="invoice-id">{invoice.invoice_number}</h6>
                  <p className="company-name">{invoice.company_name}</p>
                  <div className="divider"></div>
                  <div className="invoice-meta">
                    <div>
                      <span>Due Date</span>
                      <strong>{formatDate(invoice.due_date)}</strong>
                    </div>
                    <div>
                      <span>Amount</span>
                      <strong>{invoice.amount !== null ? `₹ ${invoice.amount}` : "--"}</strong>
                    </div>
                  </div>
                </div>

                <div className="card-actions">
                  <button className="btn action-btn" title="View" type="button" onClick={() => setViewPdfUrl(invoice.invoice_url)}>
                    <Eye size={16} />
                  </button>

                  <a href={invoice.invoice_url} target="_blank" rel="noreferrer" className="btn action-btn" title="Download PDF" download>
                    <Download size={16} />
                  </a>

                  <button className="btn action-btn edit-btn" title="Edit" type="button" onClick={() => setEditInvoice(invoice)}>
                    <Pencil size={16} />
                  </button>

                  <button className="btn action-btn trash-btn" title="Delete" type="button" onClick={() =>
                    setConfirmModal({
                      show: true,
                      invoiceId: invoice.id,
                    })
                  }
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            className="ip-empty"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 0.68, 0, 1.2] }}
          >
            <div className="ip-empty-icon-wrap">
              <FileText size={32} />
            </div>

            <h5 className="ip-empty-title">
              {statusFilter === "All"
                ? "No Invoices Uploaded Yet"
                : `No ${statusFilter === "1" ? "Paid" : "Unpaid"} Invoices Found`}
            </h5>

            <p className="ip-empty-desc">
              {statusFilter === "All"
                ? "Keep track of all your billing records and payments in one place. Upload or generate your first invoice to get started."
                : `There are currently no invoices matching the "${statusFilter === "1" ? "Paid" : "Unpaid"}" filter.`}
            </p>

            <div className="ip-empty-actions">
              {statusFilter !== "All" && (
                <button
                  className="ip-secondary-btn"
                  onClick={() => setStatusFilter("All")}
                  type="button"
                >
                  Show All Invoices
                </button>
              )}

              <motion.button
                className="ip-primary-btn"
                onClick={() => setUploadModel(true)}
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Plus size={16} /> Create Invoice
              </motion.button>
            </div>

            <div className="ip-empty-tips">
              <span className="ip-tip-item">
                <Sparkles size={14} /> Instant PDF Preview
              </span>
              <span className="ip-tip-item">
                <CheckCircle2 size={14} /> Easy Status Tracking
              </span>
              <span className="ip-tip-item">
                <ShieldCheck size={14} /> Secure Records
              </span>
            </div>
          </motion.div>
        )}
      </motion.div>
    </>
  );
}

